import { SyntheticEvent, useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import ReactSelect from 'react-select'

import TextArea from 'components/atoms/Textarea'
import {
  CancellationReason,
  cancellationReasons,
  getCancellationReasonTemplate,
  hstDisqualifyingConditions,
  isHstDisqualifiedCancellationReason,
  isOtherCancellationReason,
} from 'constants/cancellationReasons'

export interface CancelDialogSubmitPayload {
  description: string
  directMessage: string
  reason: CancellationReason
  sendDirectMessage: boolean
  disqualifyingConditions?: string[]
}

interface IProps {
  show?: boolean
  onClose: () => void
  onSubmit: (payload: CancelDialogSubmitPayload) => Promise<void>
  context?: 'DME' | 'LAB'
  cannotCancelReason?: string
  disqualifyingConditionDefaults?: string[]
  testType?: string
}

export const CancelDialog = ({
  show,
  onClose,
  onSubmit,
  context = 'LAB',
  cannotCancelReason,
  disqualifyingConditionDefaults,
  testType,
}: IProps) => {
  const [description, setDescription] = useState('')
  const [directMessage, setDirectMessage] = useState('')
  const [reason, setReason] = useState<CancellationReason | null>(null)
  const [disqualifyingConditions, setDisqualifyingConditions] = useState<string[]>([])

  // The HST disqualification reason (and its exclusion-letter workflow) only applies to HST orders
  const availableReasons = cancellationReasons.filter(
    (cancellationReason) =>
      testType === 'HST' || !isHstDisqualifiedCancellationReason(cancellationReason),
  )

  const otherSelected = isOtherCancellationReason(reason)
  const disqualifiedSelected = isHstDisqualifiedCancellationReason(reason)
  const shouldSendDirectMessage =
    context === 'LAB'
      ? !!reason && !otherSelected && !!getCancellationReasonTemplate(reason)
      : !!description.trim()
  const requiresDescription = otherSelected
  const cantSubmit =
    !reason
    || (requiresDescription && !description.trim())
    || (shouldSendDirectMessage && context === 'LAB' && !directMessage.trim())
    || (disqualifiedSelected && !disqualifyingConditions.length)

  const toggleDisqualifyingCondition = (label: string) =>
    setDisqualifyingConditions((prev) =>
      prev.includes(label) ? prev.filter((condition) => condition !== label) : [...prev, label],
    )

  useEffect(() => {
    if (!show) {
      setDescription('')
      setDirectMessage('')
      setReason(null)
      setDisqualifyingConditions([])
    }
  }, [show])

  useEffect(() => {
    setDisqualifyingConditions(
      disqualifiedSelected ? disqualifyingConditionDefaults ?? [] : [],
    )
  }, [disqualifiedSelected])

  useEffect(() => {
    if (context !== 'LAB') return

    if (!reason || otherSelected) {
      setDirectMessage('')
      return
    }

    setDirectMessage(getCancellationReasonTemplate(reason))
  }, [context, otherSelected, reason])

  return (
    <Modal centered show={!!show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>
          {cannotCancelReason ? 'ORDER CANNOT BE CANCELLED' : 'CANCEL ORDER?'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cannotCancelReason ? (
          <div>{cannotCancelReason}</div>
        ) : (
          <>
            <span className="text-bold text-gray">Attention</span>
            <br />
            <br />
            Are you sure you want to cancel this order?
            <br />
            <br />
            <span className="text-bold text-gray">Choose a Reason for Order Cancellation</span>
            <br />
            <ReactSelect
              className="basic-single w-100 mt-2 mr-3"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Choose a reason..."
              name="reason"
              onChange={(value) => setReason(value)}
              options={availableReasons}
              value={
                availableReasons?.find(
                  (cancellationReason) => cancellationReason.value === reason?.value,
                ) ?? null
              }
            />
            {disqualifiedSelected && (
              <div className="mt-3">
                <span className="text-bold text-gray">
                  Select the Disqualifying Condition(s)
                </span>
                {hstDisqualifyingConditions.map((condition) => (
                  <div className="form-check mt-1" key={condition.paperworkField}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`dq-condition-${condition.paperworkField}`}
                      checked={disqualifyingConditions.includes(condition.label)}
                      onChange={() => toggleDisqualifyingCondition(condition.label)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`dq-condition-${condition.paperworkField}`}>
                      {condition.label}
                    </label>
                  </div>
                ))}
              </div>
            )}
            <br />
            {context === 'LAB' ? (
              <>
                Please enter any cancellation notes below. This will be stored with the cancelled
                order and does not change the direct message template sent to the DME Office.
                <br />
                <br />
                <TextArea
                  handleChange={(e: SyntheticEvent) => setDescription(e.target.value)}
                  name="cancelReason"
                  label="Cancellation Notes"
                  value={description}
                  rows={5}
                  labelClassName="text-gray"
                />
                {!!reason && !otherSelected && (
                  <TextArea
                    handleChange={() => undefined}
                    name="directMessage"
                    label="Direct Message Preview"
                    value={directMessage}
                    rows={5}
                    disabled
                    labelClassName="text-gray"
                  />
                )}
              </>
            ) : (
              <>
                Please enter a short summary of the reason below that will be used to send a
                message to the Lab. <br />
                <br />
                <TextArea
                  handleChange={(e: SyntheticEvent) => setDescription(e.target.value)}
                  name="cancelReason"
                  label="Message Notes"
                  value={description}
                  rows={5}
                  labelClassName="text-gray"
                />
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {cannotCancelReason ? (
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose}>
              No
            </Button>
            <Button
              autoFocus
              variant="primary"
              disabled={cantSubmit}
              onClick={async () => {
                if (!reason) return

                await onSubmit({
                  description,
                  directMessage,
                  reason,
                  sendDirectMessage: shouldSendDirectMessage,
                  disqualifyingConditions: disqualifiedSelected
                    ? disqualifyingConditions
                    : undefined,
                })
              }}>
              Yes
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
